import React from 'react';
import { getAllLayoutStyles } from './layoutStyles';
import { motion } from 'framer-motion';

/**
 * LayoutStylePicker — visual picker showing mini previews of each layout style.
 * Used inside PlatformSettings > Appearance section.
 */
const LayoutStylePicker = ({ selectedStyle = 'classic', onSelect, primaryColor = '#1E3A5F' }) => {
  const styles = getAllLayoutStyles();

  const renderMiniPreview = (style) => {
    const sidebarMode = style.sidebar.sidebarMode || 'vertical';

    // Horizontal layout — top bar only, no sidebar
    if (sidebarMode === 'horizontal') {
      return (
        <div className="flex flex-col h-full">
          {/* Top nav bar with logo + items */}
          <div
            className="flex items-center px-2 gap-2"
            style={{
              height: '20%',
              backgroundColor: style.preview.header,
              borderBottom: `1px solid ${style.navbar.border}`,
            }}
          >
            <div className="w-3 h-3 rounded" style={{ backgroundColor: primaryColor }} />
            <div className="h-1 rounded-full w-6" style={{ backgroundColor: style.preview.headerText, opacity: 0.3 }} />
            <div className="flex gap-1 ml-1">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-1.5 rounded-sm"
                  style={{
                    width: i === 1 ? '12px' : '10px',
                    backgroundColor: i === 1 ? primaryColor : style.preview.headerText,
                    opacity: i === 1 ? 1 : 0.2,
                  }}
                />
              ))}
            </div>
            <div className="flex-1" />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.2 }} />
          </div>
          {/* Content */}
          <div className="flex-1 p-2" style={{ backgroundColor: style.layout.contentBg }}>
            <div className="space-y-1.5">
              <div className="h-2 rounded-sm w-2/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.15 }} />
              <div className="h-1.5 rounded-sm w-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.08 }} />
              <div className="flex gap-1 mt-2">
                <div className="h-6 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                <div className="h-6 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                <div className="h-6 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Compact layout — thin icon-only sidebar
    if (sidebarMode === 'compact') {
      return (
        <div className="flex h-full">
          {/* Slim icon sidebar */}
          <div
            className="flex flex-col items-center py-2 gap-1.5"
            style={{
              width: '14%',
              backgroundColor: style.preview.sidebar,
              borderRight: `1px solid ${style.sidebar.border}`,
            }}
          >
            <div className="w-3 h-3 rounded" style={{ backgroundColor: primaryColor }} />
            <div className="h-px w-3 my-0.5" style={{ backgroundColor: style.sidebar.border }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="w-2.5 h-2.5 rounded"
                style={{
                  backgroundColor: i === 1 ? primaryColor : style.preview.sidebarText,
                  opacity: i === 1 ? 1 : 0.3,
                }}
              />
            ))}
          </div>
          {/* Content */}
          <div className="flex-1 flex flex-col">
            <div
              className="flex items-center px-2"
              style={{
                height: '16%',
                backgroundColor: style.preview.header,
                borderBottom: `1px solid ${style.navbar.border}`,
              }}
            >
              <div className="h-1 rounded-full w-1/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.3 }} />
              <div className="flex-1" />
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.2 }} />
              </div>
            </div>
            <div className="flex-1 p-2" style={{ backgroundColor: style.layout.contentBg }}>
              <div className="space-y-1.5">
                <div className="h-2 rounded-sm w-2/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.15 }} />
                <div className="flex gap-1 mt-1.5">
                  <div className="h-5 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                  <div className="h-5 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Floating layout — sidebar with margin/rounded
    if (sidebarMode === 'floating') {
      return (
        <div className="flex h-full p-1 gap-1" style={{ backgroundColor: style.layout.contentBg }}>
          {/* Floating sidebar */}
          <div
            className="flex flex-col py-1.5 px-1 gap-1 rounded-md"
            style={{
              width: '28%',
              backgroundColor: style.preview.sidebar,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <div className="flex items-center gap-1 px-0.5 mb-0.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: primaryColor }} />
              <div className="h-1 rounded-full flex-1" style={{ backgroundColor: style.preview.sidebarText, opacity: 0.4 }} />
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-1.5 rounded-sm mx-0.5"
                style={{
                  backgroundColor: i === 1 ? primaryColor : style.sidebar.hoverBg,
                  opacity: i === 1 ? 1 : 0.5,
                }}
              />
            ))}
          </div>
          {/* Content */}
          <div className="flex-1 flex flex-col gap-1">
            <div
              className="flex items-center px-1.5 rounded-md"
              style={{
                height: '18%',
                backgroundColor: style.preview.header,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <div className="h-1 rounded-full w-1/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.3 }} />
              <div className="flex-1" />
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.2 }} />
            </div>
            <div className="flex-1 p-1.5 rounded-md" style={{ backgroundColor: '#FFFFFF' }}>
              <div className="space-y-1">
                <div className="h-1.5 rounded-sm w-2/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.12 }} />
                <div className="h-1 rounded-sm w-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                <div className="flex gap-1 mt-1">
                  <div className="h-4 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.04 }} />
                  <div className="h-4 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.04 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Default vertical sidebar
    return (
      <div className="flex h-full">
        {/* Mini Sidebar */}
        <div
          className="flex flex-col py-2 px-1.5 gap-1.5"
          style={{
            width: '30%',
            backgroundColor: style.preview.sidebar,
            borderRight: `1px solid ${style.sidebar.border}`,
          }}
        >
          <div className="flex items-center gap-1 px-0.5 mb-1">
            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: primaryColor }} />
            <div className="h-1.5 rounded-full flex-1" style={{ backgroundColor: style.preview.sidebarText, opacity: 0.6 }} />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-2 rounded-sm"
              style={{
                backgroundColor: i === 1 ? primaryColor : style.sidebar.hoverBg,
                opacity: i === 1 ? 1 : 0.5,
              }}
            />
          ))}
        </div>
        {/* Mini Content Area */}
        <div className="flex-1 flex flex-col">
          <div
            className="flex items-center px-2"
            style={{
              height: '16%',
              backgroundColor: style.preview.header,
              borderBottom: `1px solid ${style.navbar.border}`,
              boxShadow: style.navbar.style === 'shadow' ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
              backdropFilter: style.navbar.style === 'glass' ? 'blur(4px)' : 'none',
            }}
          >
            <div className="h-1 rounded-full w-1/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.3 }} />
            <div className="flex-1" />
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.2 }} />
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.2 }} />
            </div>
          </div>
          <div className="flex-1 p-2" style={{ backgroundColor: style.layout.contentBg }}>
            <div className="space-y-1.5">
              <div className="h-2 rounded-sm w-2/3" style={{ backgroundColor: style.preview.headerText, opacity: 0.15 }} />
              <div className="h-1.5 rounded-sm w-full" style={{ backgroundColor: style.preview.headerText, opacity: 0.08 }} />
              <div className="h-1.5 rounded-sm w-5/6" style={{ backgroundColor: style.preview.headerText, opacity: 0.08 }} />
              <div className="flex gap-1 mt-1.5">
                <div className="h-4 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
                <div className="h-4 rounded flex-1" style={{ backgroundColor: style.preview.headerText, opacity: 0.06 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {styles.map((style) => {
        const isSelected = selectedStyle === style.id;
        return (
          <motion.button
            key={style.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelect(style.id)}
            className={`relative w-full text-left rounded-xl border-2 overflow-hidden transition-all focus:outline-none ${
              isSelected
                ? 'ring-2 ring-offset-2 shadow-lg'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm hover:shadow-md'
            }`}
            style={
              isSelected
                ? { borderColor: primaryColor, ringColor: primaryColor, '--tw-ring-color': primaryColor }
                : {}
            }
          >
            {/* Mini Layout Preview */}
            <div className="p-2.5">
              <div
                className="rounded-lg overflow-hidden border border-gray-100 dark:border-gray-700"
                style={{ height: '90px' }}
              >
                {renderMiniPreview(style)}
              </div>
            </div>

            {/* Label */}
            <div className="px-2.5 pb-2.5 pt-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {style.name}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight line-clamp-2">
                    {style.description}
                  </p>
                </div>
                {isSelected && (
                  <div
                    className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ml-1"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

export default LayoutStylePicker;
